/**
 * animPlayerPool — independent players (THREE.AnimationAction) for one clip.
 *
 * The graph plays a clip per Play Clip NODE, not per clip name: two state
 * machines on `idle` each need their own playhead, speed and loop mode, and a
 * transition from a state on `walk` to another state on `walk` crossfades the
 * outgoing copy into a fresh one rather than sharing a single action.
 *
 * `mixer.clipAction(clip)` cannot provide that: it caches ONE action per clip
 * object and hands the same one back on every call. So each player is built on
 * a VIEW of the source clip — a new AnimationClip over the SAME track objects.
 * The view has its own uuid, so the mixer gives it its own action through the
 * documented path, while the keyframe arrays are shared rather than copied.
 * (Constructing `new AnimationAction(mixer, clip)` directly also works, but on
 * first play the mixer overwrites its per-clip cache with that action, so a
 * later `clipAction(clip)` — every template lookup in the app — would return a
 * graph player instead of the template.)
 *
 * Players are pooled per source clip. A released player is stopped and kept
 * for the next acquire: measured on a 250-bone clip in r186, a fresh player
 * costs ~15 µs to make and ~38 KB to keep, a reused one ~1 µs, and a stopped
 * player costs nothing per frame (the mixer only walks active actions). So the
 * pool never holds more players for a clip than ever played at once.
 *
 * One pool per mixer, found through `playerPoolFor(mixer)`, so a character
 * switch (a new mixer) starts a new pool and the old one goes with its mixer.
 */

import { AnimationClip, LoopOnce } from 'three'

const _pools = new WeakMap()

/**
 * Where `action` is in its clip, written into `out` ({ time, duration,
 * remaining, ratio }) — what the Time Remaining / Ratio rules read. A looping
 * action wraps; a one-shot one is clamped, so a clip holding its final frame
 * reports remaining 0 and ratio 1 (wrapping it read as the START, remaining a
 * full duration). Returns false for a clip with no duration. No allocation.
 */
export function playerTiming(action, out) {
  const duration = action.getClip().duration
  if (!(duration > 0)) return false
  let time = action.time
  if (action.loop === LoopOnce) time = time < 0 ? 0 : (time > duration ? duration : time)
  else time = time % duration
  out.time      = time
  out.duration  = duration
  out.remaining = duration - time
  out.ratio     = time / duration
  return true
}

/** The pool belonging to `mixer`, created on first use. */
export function playerPoolFor(mixer) {
  let pool = _pools.get(mixer)
  if (!pool) {
    pool = createPlayerPool(mixer)
    _pools.set(mixer, pool)
  }
  return pool
}

/**
 * @param {THREE.AnimationMixer} mixer
 * @returns {{
 *   acquire(source: THREE.AnimationClip): Player,
 *   release(player: Player): void,
 *   forget(source: THREE.AnimationClip): void,
 *   stats(): { live: number, idle: number, created: number },
 *   dispose(): void,
 * }}
 *
 * A Player is `{ source, clip, action, pool, live }`: `source` is the clip it
 * plays, `clip` the view its action is bound to.
 */
export function createPlayerPool(mixer) {
  const free = new Map()  // source clip → Player[] (stopped, ready to reuse)
  const all = new Set()   // every player not yet dropped, for dispose()
  const gone = new WeakSet()  // sources forget() was told were replaced
  let live = 0, created = 0

  // Out of the pool AND the mixer's cache: a player of a replaced clip is
  // never acquired again, so keeping it only holds its action and keyframes.
  const drop = (p) => {
    p.action.stop()
    mixer.uncacheClip(p.clip)
    all.delete(p)
  }

  const pool = {
    acquire(source) {
      const list = free.get(source)
      let p = list && list.length ? list.pop() : null
      if (!p) {
        const view = new AnimationClip(source.name, source.duration, source.tracks, source.blendMode)
        view.userData = source.userData
        p = { source, clip: view, action: mixer.clipAction(view), pool, live: false }
        all.add(p)
        created++
      }
      p.live = true
      live++
      return p
    },

    release(p) {
      if (!p || !p.live) return
      p.live = false
      live--
      if (gone.has(p.source)) { drop(p); return }
      p.action.stop()
      let list = free.get(p.source)
      if (!list) { list = []; free.set(p.source, list) }
      list.push(p)
    },

    /**
     * `source` was replaced (a clip regenerated under the same name): drop its
     * idle players now, and its live ones as they are released. Without this
     * every regeneration leaves its copies in the pool and the mixer for good.
     */
    forget(source) {
      if (!source) return
      gone.add(source)
      for (const p of free.get(source) ?? []) drop(p)
      free.delete(source)
    },

    stats() {
      let idle = 0
      for (const list of free.values()) idle += list.length
      return { live, idle, created }
    },

    dispose() {
      for (const p of all) {
        p.action.stop()
        mixer.uncacheClip(p.clip)
      }
      all.clear()
      free.clear()
      live = 0
    },
  }
  return pool
}
